import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { Separator } from "../components/ui/separator";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, XCircle, AlertCircle, Clock } from "lucide-react";

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen" data-testid="refund-policy-page">
      <Navigation />
      
      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">Refund & Returns Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>
          
          {/* ACL Notice */}
          <Card className="mb-8 border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-primary" />
                Australian Consumer Law Rights
              </h3>
              <p className="text-muted-foreground">
                This policy operates alongside, and does not limit, your rights under the Australian Consumer Law. 
                You are entitled to a replacement or refund for a major failure, and compensation for other 
                reasonably foreseeable loss or damage.
              </p>
            </CardContent>
          </Card>

          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-medium mb-4">1. Overview</h2>
              <p className="text-muted-foreground leading-relaxed">
                At Art By Arawol, we take pride in the quality of our authentic Aboriginal artwork. Due to 
                the unique nature of art, we have specific policies regarding returns and refunds. Please read 
                this policy carefully before making a purchase.
              </p>
            </section>

            <Separator />

            {/* Quick Reference Cards */}
            <section>
              <h2 className="text-2xl font-medium mb-6">2. At a Glance</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium mb-1">Damaged in Transit</h4>
                        <p className="text-sm text-muted-foreground">Full refund or replacement</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium mb-1">Wrong Item Sent</h4>
                        <p className="text-sm text-muted-foreground">Immediate correction at our cost</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium mb-1">Faulty Product</h4>
                        <p className="text-sm text-muted-foreground">Replacement or refund per ACL</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium mb-1">Change of Mind</h4>
                        <p className="text-sm text-muted-foreground">Not accepted for original artworks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">3. Original Artworks</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Original artworks are unique, one-of-a-kind pieces. Due to their nature:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Change of mind returns are not accepted</strong> for original artworks</li>
                <li>We encourage viewing artwork in person where possible, or requesting additional photos/videos before purchase</li>
                <li>If an original arrives damaged, we will work with you on restoration options or provide a full refund</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">4. Limited Edition Prints</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For limited edition prints:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Returns accepted within <strong>14 days</strong> of delivery if unopened and in original packaging</li>
                <li>Prints must be in resalable condition</li>
                <li>Return shipping costs are the customer's responsibility for change of mind returns</li>
                <li>A restocking fee of 10% may apply</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">5. Damaged or Defective Items</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If your item arrives damaged or defective:
              </p>
              <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
                <li>Contact us within <strong>48 hours</strong> of delivery</li>
                <li>Provide photos of the damage and packaging</li>
                <li>Do not dispose of packaging until the claim is resolved</li>
                <li>We will arrange replacement or full refund at our discretion</li>
                <li>Return shipping for damaged items is covered by us</li>
              </ol>
              <div className="mt-4 p-4 bg-muted/50 rounded-xl flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Claims for damage must be lodged within 48 hours of delivery to allow us to file claims 
                  with our shipping insurers.
                </p>
              </div>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">6. Auction Items</h2>
              <p className="text-muted-foreground leading-relaxed">
                Items purchased through our silent auction are final sale. All auction items are sold as-is 
                and described. No returns or refunds are accepted for change of mind on auction purchases. 
                Your consumer rights under the Australian Consumer Law still apply if items are faulty or 
                significantly different from description.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">7. Framing</h2>
              <p className="text-muted-foreground leading-relaxed">
                Custom framing is non-refundable as it is made to order. If framing arrives damaged, we will 
                repair or replace the framing at no additional cost. Please inspect framing carefully upon 
                delivery and report any issues within 48 hours.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">8. How to Request a Return</h2>
              <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
                <li>Email us at <strong>returns@arawol.art</strong> with your order number</li>
                <li>Describe the reason for return and include photos if applicable</li>
                <li>Wait for our return authorization (usually within 2 business days)</li>
                <li>Ship the item using a tracked and insured service</li>
                <li>Refund will be processed within 5-7 business days of receiving the return</li>
              </ol>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">9. Refund Methods</h2>
              <p className="text-muted-foreground leading-relaxed">
                Refunds will be issued to the original payment method:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li><strong>Credit/Debit Card:</strong> 5-10 business days to appear on statement</li>
                <li><strong>PayPal:</strong> 3-5 business days</li>
                <li><strong>Store Credit:</strong> Issued immediately upon request</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">10. Exchanges</h2>
              <p className="text-muted-foreground leading-relaxed">
                We do not offer direct exchanges. If you wish to exchange an item, please return the original 
                item for a refund and place a new order for the desired item.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">11. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                For return requests or questions about this policy:
              </p>
              <div className="mt-4 p-4 bg-card rounded-xl border border-border">
                <p className="text-muted-foreground">
                  <strong>Art By Arawol Returns</strong><br />
                  Email: returns@arawol.art<br />
                  Response time: Within 2 business days
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
